/**
 * 把 POST /v1/responses 的 SSE 折成一条有序 block 列表。
 * 未知事件忽略。确认类结果只在 output_item.done 写入完整 output。
 */

export type StreamTagKind = 'thinking' | 'tool' | 'mcp' | 'shell';
export type StreamTagStatus = 'running' | 'done' | 'failed';

export interface StreamTextBlock {
  id: string;
  type: 'text';
  text: string;
}

export interface StreamTagBlock {
  id: string;
  type: 'tag';
  kind: StreamTagKind;
  title: string;
  body: string;
  status: StreamTagStatus;
  args?: string;
  output?: string;
}

export interface StreamAskBlock {
  id: string;
  type: 'ask';
  questionId: string;
  question: string;
  options: string[];
  schema?: Record<string, unknown>;
  answer?: string;
}

export type StreamBlock = StreamTextBlock | StreamTagBlock | StreamAskBlock;

export interface ResponsesStreamState {
  blocks: StreamBlock[];
  responseId?: string;
  sessionId?: string;
  turnId?: string;
  failed: boolean;
  errorMessage?: string;
  textSeq: number;
}

export function createResponsesStreamState(): ResponsesStreamState {
  return { blocks: [], failed: false, textSeq: 0 };
}

export function plainTextFromBlocks(blocks: StreamBlock[]): string {
  return blocks
    .filter((block): block is StreamTextBlock => block.type === 'text')
    .map((block) => block.text)
    .join('\n')
    .trim();
}

/** 本轮仍在输出时，从后往前找第一条还没写 answer 的 ask。 */
export function findPendingAsk(blocks: StreamBlock[], live: boolean): StreamAskBlock | null {
  if (!live) return null;
  for (let index = blocks.length - 1; index >= 0; index -= 1) {
    const block = blocks[index];
    if (block.type === 'ask' && block.answer == null) return block;
  }
  return null;
}

export function settleStreamTags(blocks: StreamBlock[], status: 'done' | 'failed'): StreamBlock[] {
  return blocks.map((block) => {
    if (block.type !== 'tag' || block.status !== 'running') return block;
    return { ...block, status };
  });
}

export function withAskAnswers(previous: StreamBlock[] | undefined, next: StreamBlock[]): StreamBlock[] {
  if (!previous?.length) return next;
  const answered = new Map<string, string>();
  previous.forEach((block) => {
    if (block.type === 'ask' && block.answer != null) answered.set(block.questionId, block.answer);
  });
  if (!answered.size) return next;
  return next.map((block) => {
    if (block.type !== 'ask' || block.answer != null) return block;
    const answer = answered.get(block.questionId);
    return answer != null ? { ...block, answer } : block;
  });
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function asText(value: unknown): string | undefined {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value == null) return undefined;
  try {
    return JSON.stringify(value);
  } catch {
    return undefined;
  }
}

function errorText(value: unknown): string {
  if (typeof value === 'string') return value;
  const record = asRecord(value);
  return asString(record?.message) || asString(record?.code);
}

function shortName(name: string): string {
  const parts = name.split('__').filter(Boolean);
  return parts.length ? parts[parts.length - 1] : name;
}

function chunk(data: Record<string, unknown> | null): string {
  if (!data) return '';
  if (typeof data.delta === 'string') return data.delta;
  if (typeof data.text === 'string') return data.text;
  return '';
}

function hasText(blocks: StreamBlock[]): boolean {
  return blocks.some((block) => block.type === 'text' && block.text.trim());
}

function appendText(state: ResponsesStreamState, piece: string): ResponsesStreamState {
  if (!piece) return state;
  const last = state.blocks[state.blocks.length - 1];
  if (last?.type === 'text') {
    const blocks = state.blocks.slice();
    blocks[blocks.length - 1] = { ...last, text: last.text + piece };
    return { ...state, blocks };
  }
  return {
    ...state,
    textSeq: state.textSeq + 1,
    blocks: [...state.blocks, { id: `text-${state.textSeq}`, type: 'text', text: piece }],
  };
}

function upsertTag(
  state: ResponsesStreamState,
  id: string,
  kind: StreamTagKind,
  patch: Partial<Pick<StreamTagBlock, 'title' | 'body' | 'status' | 'args' | 'output'>>,
): ResponsesStreamState {
  const index = state.blocks.findIndex((block) => block.id === id);
  if (index < 0) {
    const tag: StreamTagBlock = {
      id,
      type: 'tag',
      kind,
      title: patch.title ?? '',
      body: patch.body ?? '',
      status: patch.status ?? 'running',
      ...(patch.args != null ? { args: patch.args } : {}),
      ...(patch.output != null ? { output: patch.output } : {}),
    };
    return { ...state, blocks: [...state.blocks, tag] };
  }
  const current = state.blocks[index];
  if (current.type !== 'tag') return state;
  const blocks = state.blocks.slice();
  blocks[index] = {
    ...current,
    ...patch,
    kind,
    title: patch.title || current.title,
  };
  return { ...state, blocks };
}

function appendTagBody(state: ResponsesStreamState, id: string, kind: StreamTagKind, piece: string): ResponsesStreamState {
  if (!piece) return upsertTag(state, id, kind, { status: 'running' });
  const current = state.blocks.find((block) => block.id === id);
  const body = current?.type === 'tag' ? current.body + piece : piece;
  return upsertTag(state, id, kind, { body, status: 'running' });
}

function appendTagArgs(state: ResponsesStreamState, id: string, kind: StreamTagKind, piece: string, title?: string): ResponsesStreamState {
  const current = state.blocks.find((block) => block.id === id);
  const args = (current?.type === 'tag' ? current.args ?? '' : '') + piece;
  return upsertTag(state, id, kind, {
    status: 'running',
    ...(title ? { title } : {}),
    ...(piece ? { args } : {}),
  });
}

function itemRecord(data: Record<string, unknown> | null): Record<string, unknown> | null {
  return asRecord(data?.item);
}

function tagId(data: Record<string, unknown> | null, fallback: string): string {
  const item = itemRecord(data);
  const outputIndex = data?.output_index;
  return asString(item?.id)
    || asString(data?.item_id)
    || (outputIndex != null ? `${fallback}-${String(outputIndex)}` : fallback);
}

function resolveTagId(
  state: ResponsesStreamState,
  data: Record<string, unknown> | null,
  kind: StreamTagKind,
  fallback: string,
): string {
  const id = tagId(data, fallback);
  if (state.blocks.some((block) => block.id === id)) return id;
  for (let index = state.blocks.length - 1; index >= 0; index -= 1) {
    const block = state.blocks[index];
    if (block.type === 'tag' && block.kind === kind) return block.id;
  }
  return id;
}

function captureTurn(state: ResponsesStreamState, data: unknown): ResponsesStreamState {
  const record = asRecord(data);
  const response = asRecord(record?.response) ?? record;
  const nerogate = asRecord(response?.nerogate) ?? asRecord(record?.nerogate);
  return {
    ...state,
    responseId: asString(response?.id) || state.responseId,
    sessionId: asString(nerogate?.sessionId) || state.sessionId,
    turnId: asString(nerogate?.turnId) || asString(response?.id) || state.turnId,
  };
}

function optionLabel(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  const record = asRecord(value);
  if (!record) return '';
  return asString(record.label) || asString(record.title) || asString(record.value) || asString(record.text);
}

function optionList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map(optionLabel).filter(Boolean);
}

function normalizeAsk(data: unknown): StreamAskBlock | null {
  const record = asRecord(data);
  if (!record) return null;
  const questions = Array.isArray(record.questions) ? asRecord(record.questions[0]) : null;
  const item = asRecord(record.item);
  const nerogate = asRecord(record.nerogate);
  const schema = asRecord(record.schema) ?? asRecord(questions?.schema) ?? undefined;
  const questionId = asString(record.questionId)
    || asString(item?.questionId)
    || asString(nerogate?.questionId)
    || asString(record.id)
    || asString(questions?.id);
  if (!questionId) return null;
  const question = asString(record.question)
    || asString(questions?.question)
    || asString(questions?.header)
    || asString(schema?.title);
  const options = optionList(record.options).length
    ? optionList(record.options)
    : optionList(questions?.options);
  return {
    id: `ask-${questionId}`,
    type: 'ask',
    questionId,
    question,
    options,
    ...(schema ? { schema } : {}),
  };
}

function upsertAsk(state: ResponsesStreamState, ask: StreamAskBlock): ResponsesStreamState {
  const index = state.blocks.findIndex((block) => block.type === 'ask' && block.questionId === ask.questionId);
  if (index < 0) return { ...state, blocks: [...state.blocks, ask] };
  const current = state.blocks[index];
  if (current.type !== 'ask') return state;
  const blocks = state.blocks.slice();
  blocks[index] = {
    ...current,
    question: ask.question || current.question,
    options: ask.options.length ? ask.options : current.options,
    ...(ask.schema ? { schema: ask.schema } : {}),
  };
  return { ...state, blocks };
}

function completedText(data: unknown): string {
  const record = asRecord(data);
  const response = asRecord(record?.response) ?? record;
  const output = response?.output;
  if (!Array.isArray(output)) return '';
  const parts: string[] = [];
  output.forEach((item) => {
    const content = asRecord(item)?.content;
    if (!Array.isArray(content)) return;
    content.forEach((part) => {
      const text = asString(asRecord(part)?.text);
      if (text) parts.push(text);
    });
  });
  return parts.join('');
}

function applyOutputDone(state: ResponsesStreamState, data: Record<string, unknown>): ResponsesStreamState {
  const item = itemRecord(data);
  const itemType = asString(item?.type);
  if (itemType !== 'mcp_call' && itemType !== 'function_call') return state;
  const kind: StreamTagKind = itemType === 'mcp_call' ? 'mcp' : 'tool';
  const id = resolveTagId(state, data, kind, kind);
  const name = shortName(asString(item?.name));
  const args = asText(item?.arguments);
  const output = asText(item?.output);
  const failed = item?.status === 'failed' || item?.error != null;
  return upsertTag(state, id, kind, {
    ...(name ? { title: name } : {}),
    ...(args != null ? { args } : {}),
    ...(output != null ? { output } : {}),
    status: failed ? 'failed' : 'done',
  });
}

export function applyResponsesEvent(
  state: ResponsesStreamState,
  event: string,
  data: unknown,
): ResponsesStreamState {
  const name = event.trim();
  const record = asRecord(data);
  if (!name) return state;

  if (
    name === 'error'
    || name === 'response.failed'
    || name === 'response.error'
    || (record?.error != null && !name.startsWith('response.'))
  ) {
    return {
      ...state,
      failed: true,
      errorMessage: errorText(record?.error) || errorText(data) || state.errorMessage,
    };
  }

  if (name === 'response.created') return captureTurn(state, data);

  if (name === 'response.output_text.delta') return appendText(state, chunk(record));

  if (name === 'response.output_item.added') {
    const item = itemRecord(record);
    const itemType = asString(item?.type);
    if (itemType === 'reasoning') {
      return upsertTag(state, tagId(record, 'thinking'), 'thinking', { title: '', status: 'running' });
    }
    if (itemType === 'function_call') {
      const title = shortName(asString(item?.name));
      const args = asText(item?.arguments);
      return upsertTag(state, tagId(record, 'tool'), 'tool', {
        title,
        status: 'running',
        ...(args ? { args } : {}),
      });
    }
    return state;
  }

  if (name === 'response.reasoning_text.delta') {
    return appendTagBody(state, resolveTagId(state, record, 'thinking', 'thinking'), 'thinking', chunk(record));
  }

  if (name === 'response.reasoning_text.done') {
    const id = resolveTagId(state, record, 'thinking', 'thinking');
    const current = state.blocks.find((block) => block.id === id);
    const body = current?.type === 'tag' && current.body ? current.body : (asString(record?.text) || undefined);
    return upsertTag(state, id, 'thinking', { status: 'done', ...(body != null ? { body } : {}) });
  }

  if (name === 'response.function_call_arguments.delta') {
    const title = shortName(asString(record?.name) || asString(itemRecord(record)?.name));
    return appendTagArgs(state, resolveTagId(state, record, 'tool', 'tool'), 'tool', chunk(record), title);
  }

  if (name === 'response.function_call_arguments.done') {
    const args = asText(record?.arguments);
    return upsertTag(state, resolveTagId(state, record, 'tool', 'tool'), 'tool', {
      status: 'done',
      ...(args != null ? { args } : {}),
    });
  }

  if (name === 'response.mcp_call.in_progress' || name === 'response.mcp_call_arguments.delta') {
    const title = shortName(asString(record?.name) || asString(itemRecord(record)?.name));
    if (name === 'response.mcp_call_arguments.delta') {
      return appendTagArgs(state, resolveTagId(state, record, 'mcp', 'mcp'), 'mcp', chunk(record), title);
    }
    return upsertTag(state, resolveTagId(state, record, 'mcp', 'mcp'), 'mcp', {
      status: 'running',
      ...(title ? { title } : {}),
    });
  }

  if (name === 'response.mcp_call.completed' || name === 'response.mcp_call.failed') {
    return upsertTag(state, resolveTagId(state, record, 'mcp', 'mcp'), 'mcp', {
      status: name.endsWith('failed') ? 'failed' : 'done',
    });
  }

  if (name === 'response.output_item.done') return applyOutputDone(state, record ?? {});

  if (name === 'response.nerogate.shell_output.delta') {
    return appendTagBody(state, resolveTagId(state, record, 'shell', 'shell'), 'shell', chunk(record));
  }

  if (name === 'response.nerogate.ask') {
    const ask = normalizeAsk(data);
    return ask ? upsertAsk(state, ask) : state;
  }

  if (name === 'response.completed') {
    let next = captureTurn(state, data);
    const status = asString(record?.status) || asString(asRecord(record?.response)?.status);
    if (status === 'failed') next = { ...next, failed: true, errorMessage: next.errorMessage || errorText(record?.error) };
    if (!hasText(next.blocks)) {
      const fallback = completedText(data);
      if (fallback) next = appendText(next, fallback);
    }
    return next;
  }

  return state;
}
